/**
 * Currency Context
 * Provides currency and symbol throughout the app
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getCurrencySymbol, getCurrencyName } from '../utils/currencyUtils';
import { companyApi } from '../services/api';

interface CurrencyContextType {
  currency: string;
  currencySymbol: string;
  currencyName: string;
  loading: boolean;
  updateCurrency: (newCurrency: string) => Promise<void>;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [currency, setCurrency] = useState<string>('EUR');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    loadCurrency();
  }, [user]);

  const loadCurrency = async () => {
    try {
      if (!user?.companyId) {
        setCurrency('EUR');
        setLoading(false);
        return;
      }

      const settings = await companyApi.getSettings();
      const companyCurrency = settings.currency || 'EUR';
      setCurrency(companyCurrency);
    } catch (error) {
      console.error('Error loading currency:', error);
      setCurrency('EUR'); // Default fallback
    } finally {
      setLoading(false);
    }
  };

  const updateCurrency = async (newCurrency: string) => {
    try {
      if (!user?.companyId) return;

      await companyApi.updateSettings({ currency: newCurrency });
      setCurrency(newCurrency);
    } catch (error) {
      console.error('Error updating currency:', error);
      throw error;
    }
  };

  const value: CurrencyContextType = {
    currency,
    currencySymbol: getCurrencySymbol(currency),
    currencyName: getCurrencyName(currency),
    loading,
    updateCurrency
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextType {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}

